// src/lib/security/dataProtection.ts
// Secure-ish storage facade with TTL, environment scoping, PII-safe logs,
// SSR fallback, and HMR-safe timer cleanup.
// API is backward-compatible:
//   getItem<T>(key: string): T | null
//   getItem<T>(key: string, opts: { sensitive?: boolean }): T | null    // legacy overload
//   setItem(key: string, value: string, opts?: { sensitive?: boolean; ttlSeconds?: number }): void
//   removeItem(key: string): void
//
// NOTE: "sensitive" data uses sessionStorage (short-lived, per-tab).
//       If you need *real* encryption-at-rest, we can wire SubtleCrypto + key management later.

type SecureGetOpts = { sensitive?: boolean };
type SetOpts = { sensitive?: boolean; ttlSeconds?: number };

// Envelope stored in storage
type Envelope = {
  v: 1;                 // version
  val: string;          // original string value (callers decide JSON vs raw)
  t: number;            // createdAt epoch ms
  ttl?: number;         // seconds
  s?: boolean;          // sensitive flag (hints where it was stored originally)
};

// ————————————————————————————————————————————————————————————————————————————
// env + runtime helpers
// ————————————————————————————————————————————————————————————————————————————

const ENV_LABEL: string =
  (import.meta as any)?.env?.VITE_ENV_LABEL ??
  (typeof process !== 'undefined' ? process.env?.VITE_ENV_LABEL : undefined) ??
  'UNK';

const RUNTIME_ENV: string =
  (import.meta as any)?.env?.VITE_RUNTIME_ENV ??
  (typeof process !== 'undefined' ? process.env?.VITE_RUNTIME_ENV : undefined) ??
  'unknown';

const IS_DEV = !!((import.meta as any)?.env?.DEV);

// Namespaced keys to prevent STG/PRD bleeding
const NS_LOCAL = `w3:${ENV_LABEL}:local:`;
const NS_SESSION = `w3:${ENV_LABEL}:session:`;

// SSR-safe guards
const hasWindow = typeof window !== 'undefined';
const localStore: Storage | null = hasWindow && 'localStorage' in window ? window.localStorage : null;
const sessionStore: Storage | null = hasWindow && 'sessionStorage' in window ? window.sessionStorage : null;

// In-memory fallback (SSR / restricted environments)
const memoryLocal = new Map<string, string>();
const memorySession = new Map<string, string>();

function now() {
  return Date.now();
}

function nsKey(key: string, sensitive?: boolean) {
  return (sensitive ? NS_SESSION : NS_LOCAL) + key;
}

function safeLogDev(label: string, data?: unknown) {
  if (!IS_DEV || !hasWindow) return;
  // PII-safe: never log stored values; only meta
  try {

    console.debug(`[secureStorage:${ENV_LABEL}/${RUNTIME_ENV}] ${label}`, data ?? '');
  } catch {
    /* console not available */
  }
}

// Parse stored string -> Envelope | null
function parseEnvelope(raw: string | null): Envelope | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  try {
    const json = JSON.parse(raw) as Envelope | unknown;
    if (
      typeof (json as Envelope)?.v === 'number' &&
      (json as Envelope).v === 1 &&
      typeof (json as Envelope).val === 'string' &&
      typeof (json as Envelope).t === 'number'
    ) {
      return json as Envelope;
    }
    // Back-compat: raw value without envelope (rare). Treat as immediate value with no TTL.
    return { v: 1, val: raw, t: now() };
  } catch {
    /* Raw non-JSON value: keep as-is inside envelope */
    return { v: 1, val: raw, t: now() };
  }
}

function isExpired(env: Envelope): boolean {
  if (!env.ttl || env.ttl <= 0) return false;
  return now() - env.t > env.ttl * 1000;
}

function writeTo(store: Storage | null, mem: Map<string, string>, key: string, val: string) {
  try {
    if (store) store.setItem(key, val);
    else mem.set(key, val);
  } catch {
    /* quota/full/etc -> fallback to memory */
    mem.set(key, val);
  }
}

function readFrom(store: Storage | null, mem: Map<string, string>, key: string): string | null {
  try {
    if (store) return store.getItem(key);
    return mem.get(key) ?? null;
  } catch {
    /* storage access failed, use memory */
    return mem.get(key) ?? null;
  }
}

function removeFrom(store: Storage | null, mem: Map<string, string>, key: string) {
  try {
    if (store) store.removeItem(key);
    else mem.delete(key);
  } catch {
    /* storage access failed, use memory */
    mem.delete(key);
  }
}

// ————————————————————————————————————————————————————————————————————————————
// Purge expired items periodically (HMR-safe)
// ————————————————————————————————————————————————————————————————————————————

declare global {
  interface Window {
    __secureStoragePurgeTimer?: number;
  }
}

function purgeExpired() {
  // local
  try {
    const scan = (store: Storage | null, mem: Map<string, string>, ns: string) => {
      const keys: string[] = [];
      if (store) {
        for (let i = 0; i < store.length; i++) {
          const k = store.key(i);
          if (k && k.startsWith(ns)) keys.push(k);
        }
      } else {
        for (const k of mem.keys()) if (k.startsWith(ns)) keys.push(k);
      }
      for (const k of keys) {
        const raw = readFrom(store, mem, k);
        const env = parseEnvelope(raw);
        if (!env) {
          removeFrom(store, mem, k);
          continue;
        }
        if (isExpired(env)) {
          removeFrom(store, mem, k);
        }
      }
    };
    scan(localStore, memoryLocal, NS_LOCAL);
    scan(sessionStore, memorySession, NS_SESSION);
  } catch {
    /* purge scan failed */
  }
}

if (hasWindow && !window.__secureStoragePurgeTimer) {
  window.__secureStoragePurgeTimer = window.setInterval(purgeExpired, 10 * 60 * 1000); // every 10m
}

// HMR dispose to prevent timer leaks
if ((import.meta as any)?.hot) {
  import.meta.hot.dispose(() => {
    if (hasWindow && window.__secureStoragePurgeTimer) {
      clearInterval(window.__secureStoragePurgeTimer);
      window.__secureStoragePurgeTimer = undefined;
    }
  });
}

// ————————————————————————————————————————————————————————————————————————————
// Public API (with overload compatibility)
// ————————————————————————————————————————————————————————————————————————————

export interface ISecureStorage {
  // New style (recommended)
  getItem<T = string>(key: string): T | null;
  // Old style (compat)
  getItem<T = string>(key: string, _opts: SecureGetOpts): T | null;

  setItem(key: string, value: string, opts?: SetOpts): void;
  removeItem(key: string): void;
}

class SecureStorage implements ISecureStorage {
  // Overloads
  public getItem<T = string>(key: string): T | null;
  public getItem<T = string>(key: string, _opts: SecureGetOpts): T | null;

  // Impl
  public getItem<T = string>(key: string, _opts?: SecureGetOpts): T | null {
    // Try session first (covers cases when caller omits opts but value was saved as sensitive)
    const kSession = nsKey(key, true);
    const kLocal = nsKey(key, false);

    const readCandidate = (k: string, preferSession: boolean) => {
      const raw = readFrom(preferSession ? sessionStore : localStore, preferSession ? memorySession : memoryLocal, k);
      const env = parseEnvelope(raw);
      if (!env) return null;
      if (isExpired(env)) {
        // cleanup and ignore
        removeFrom(preferSession ? sessionStore : localStore, preferSession ? memorySession : memoryLocal, k);
        safeLogDev('expired key purged', { key });
        return null;
      }
      try {
        // Caller decides whether to JSON.parse further;
        // We return as T with a cast because storage stores strings by contract.
        return (env.val as unknown) as T;
      } catch {
        /* type conversion failed */
        return null;
      }
    };

    // 1) session (sensitive) bucket
    const v1 = readCandidate(kSession, true);
    if (v1 !== null) return v1;

    // 2) local bucket
    const v2 = readCandidate(kLocal, false);
    if (v2 !== null) return v2;

    return null;
  }

  public setItem(key: string, value: string, opts?: SetOpts): void {
    const sensitive = !!opts?.sensitive;
    const ttlSeconds = opts?.ttlSeconds && opts.ttlSeconds > 0 ? Math.floor(opts.ttlSeconds) : undefined;

    const env: Envelope = { v: 1, val: value, t: now() };
    if (ttlSeconds) env.ttl = ttlSeconds;
    if (sensitive) env.s = true;

    const targetKey = nsKey(key, sensitive);
    const serialized = JSON.stringify(env);

    if (sensitive) {
      writeTo(sessionStore, memorySession, targetKey, serialized);
    } else {
      writeTo(localStore, memoryLocal, targetKey, serialized);
    }

    safeLogDev('setItem', { key, sensitive, hasTTL: !!ttlSeconds });
  }

  public removeItem(key: string): void {
    // remove from both buckets to be safe
    removeFrom(sessionStore, memorySession, nsKey(key, true));
    removeFrom(localStore, memoryLocal, nsKey(key, false));
    safeLogDev('removeItem', { key });
  }
}

export const secureStorage: ISecureStorage = new SecureStorage();
export default secureStorage;
